import { useEffect, useState } from "react";
import { Link2, Copy, Check, X, QrCode, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { humanError } from "@/lib/api";

const EXPIRY_PRESETS = [
  { label: "24 hours", hours: 24 },
  { label: "3 days", hours: 72 },
  { label: "1 week", hours: 168 },
  { label: "2 weeks", hours: 336 },
  { label: "30 days", hours: 720 },
];

function qrUrl(data) {
  // Public QR generator (open, no API key); keeps bundle small and renders
  // a scannable code without a frontend lib.
  return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(data)}`;
}

export function ShareReviewDialog({ open, onOpenChange, scopeKind, scopeId, scopeLabel }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expiryHours, setExpiryHours] = useState(168);
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    if (!scopeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/review-links?scope_kind=${scopeKind}&scope_id=${scopeId}`);
      setLinks(data.items || []);
    } catch {
      setLinks([]);
    } finally { setLoading(false); }
  }

  useEffect(() => { if (open) { setJustCreated(null); setCopied(false); load(); } /* eslint-disable-next-line */ }, [open, scopeId]);

  async function mint() {
    try {
      const { data } = await api.post("/review-links", {
        scope_kind: scopeKind, scope_id: scopeId, expires_in_hours: expiryHours,
      });
      const url = `${window.location.origin}/r/${data.token}`;
      setJustCreated({ ...data, url });
      load();
      toast.success("Review link created");
      navigator.clipboard?.writeText(url).catch(() => {});
    } catch (e) { toast.error(humanError(e)); }
  }

  async function revoke(id) {
    if (!window.confirm("Revoke this link? Anyone with it will immediately lose access.")) return;
    try {
      await api.post(`/review-links/${id}/revoke`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error(humanError(e)); }
  }

  async function copyIt() {
    if (!justCreated?.url) return;
    try {
      await navigator.clipboard.writeText(justCreated.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed — select the URL manually"); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF3FB] text-[#1F5FD8]"><Link2 className="h-4 w-4" /></span>
            Share for review
          </DialogTitle>
          <DialogDescription>
            Send {scopeKind === "campaign" ? "this campaign" : "this post"} to anyone — no account needed. They can approve or request changes directly.
          </DialogDescription>
        </DialogHeader>

        {justCreated ? (
          <div className="rounded-xl border border-[#16A46B]/30 bg-[#E8FBF1]/50 p-4" data-testid="review-link-success">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#0B5D3C]">Your link is live</div>
              <div className="text-[11px] text-[#43535E]">Expires {new Date(justCreated.expires_at).toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={justCreated.url}
                data-testid="review-link-url"
                className="w-full truncate rounded-lg border border-[#E4E9EF] bg-white px-3 py-2 text-xs text-[#0B2B33]"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={copyIt}
                data-testid="review-link-copy"
                className="inline-flex items-center gap-1 rounded-lg bg-[#1F5FD8] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1747B0]"
              >
                {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
              </button>
            </div>
            <div className="mt-4 flex items-start gap-4">
              <img
                src={qrUrl(justCreated.url)}
                alt="QR code to open the review link"
                className="h-28 w-28 rounded-lg border border-[#E4E9EF] bg-white p-1"
                data-testid="review-link-qr"
              />
              <div className="text-xs text-[#43535E]">
                <div className="mb-1 inline-flex items-center gap-1 font-semibold text-[#0B2B33]">
                  <QrCode className="h-3 w-3" /> Scan on phone
                </div>
                Reviewers scan this on their phone and tap Approve or Request changes — no app, no login. Everything they do is audit-logged just like an internal reviewer.
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Expires in</div>
            <div className="flex flex-wrap gap-1" data-testid="review-link-expiry-presets">
              {EXPIRY_PRESETS.map((p) => (
                <button
                  key={p.hours}
                  onClick={() => setExpiryHours(p.hours)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                    expiryHours === p.hours
                      ? "border-[#1F5FD8] bg-[#EEF3FB] text-[#1F5FD8]"
                      : "border-[#E4E9EF] text-[#43535E] hover:bg-[#F6F8FA]"
                  }`}
                  data-testid={`review-link-expiry-${p.hours}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="mt-4 rounded-lg bg-[#F6F8FA] p-3 text-xs text-[#43535E]">
              {scopeLabel ? <><span className="font-semibold text-[#0B2B33]">{scopeLabel}</span><br /></> : null}
              The link is shown once after you create it — nobody, not even you, can retrieve it from the server later. If you lose it, create a new one.
            </div>
          </div>
        )}

        {links.length ? (
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Active links</div>
            <ul className="divide-y divide-[#E4E9EF] rounded-lg border border-[#E4E9EF]">
              {links.map((l) => (
                <li key={l.id} className="flex items-center justify-between p-3 text-xs">
                  <div>
                    <div className="font-mono text-[11px] text-[#43535E]">#{l.id.slice(0, 8)}</div>
                    <div className="mt-0.5 text-[#8A97A0]">
                      {l.revoked ? <span className="text-[#E3252A]">Revoked</span> :
                        new Date(l.expires_at) < new Date() ? <span className="text-[#E3252A]">Expired</span> :
                          `Expires ${new Date(l.expires_at).toLocaleString()}`}
                    </div>
                  </div>
                  {!l.revoked ? (
                    <button
                      onClick={() => revoke(l.id)}
                      data-testid={`review-link-revoke-${l.id}`}
                      className="rounded-full border border-[#E4E9EF] p-1.5 text-[#E3252A] hover:bg-[#FFE6E6]"
                      title="Revoke"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm">Close</button>
          {!justCreated ? (
            <button onClick={mint} data-testid="review-link-create" className="hs-btn-primary text-sm">
              <Link2 className="mr-2 -mt-0.5 inline h-4 w-4" /> Create link
            </button>
          ) : (
            <button onClick={() => setJustCreated(null)} data-testid="review-link-create-another" className="hs-btn-primary text-sm">
              Create another
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
